import React, { useState } from "react";
import { api } from "@/lib/api";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const PIPELINES = [
  { key: "publish", title: "Publish with us", verticals: ["Writing","Editorial","Long-form"], blurb: "Get your essay, story, or chapbook into the anArtizen Journal. Editorial rigour, paid rates, and a literate audience that actually reads.", cta: "Submit your manuscript" },
  { key: "release", title: "Release with us", verticals: ["Music","Sound","Catalogue"], blurb: "We help you put records out — streaming, sync, physical, vinyl. Fair splits. You keep your masters. We help you find listeners.", cta: "Submit your record" },
  { key: "work", title: "Work with us", verticals: ["Film","Dance","Theatre","Design","Visual Art"], blurb: "Commissions, brand partnerships, residencies, performance bookings — opportunities we route to the people who can make the best of them.", cta: "Pitch a project" },
];

export default function Distribution() {
  const [active, setActive] = useState("publish");
  const cur = PIPELINES.find((p) => p.key === active);
  const [form, setForm] = useState({ name: "", email: "", discipline: "music", title: "", description: "", link: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.title || !form.description) { toast.error("All required fields"); return; }
    setSubmitting(true);
    try {
      await api.post("/distribution", { ...form, pipeline: active });
      toast.success("Submission received. We review weekly.");
      setForm({ name: "", email: "", discipline: "music", title: "", description: "", link: "" });
    } catch { toast.error("Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border">
        <SlideCorner right="DISTRIBUTION" />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-16">
          <SectionLabel>Distribution · Get the work out</SectionLabel>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[7rem] font-bold leading-[0.9] tracking-tighter">
            Three doors. <br /> <span className="hl-coral">For the work itself.</span>
          </h1>
          <p className="mt-10 max-w-2xl text-white/80 text-base md:text-lg leading-relaxed">
            Music to release. Words to publish. A film, a play, a commission. We pick what we love and put real distribution muscle behind it. <span className="hl-cyan font-medium">Submissions open every week.</span>
          </p>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12">
          <div className="grid md:grid-cols-3 gap-px bg-border">
            {PIPELINES.map((p) => (
              <button
                key={p.key}
                data-testid={`pipeline-${p.key}`}
                onClick={() => setActive(p.key)}
                className={`p-8 text-left transition ${active === p.key ? "bg-primary text-white" : "bg-background hover:bg-white/[0.03]"}`}
              >
                <p className="meta-mono mb-4" style={active === p.key ? { color: "white", opacity: 0.7 } : {}}>— PIPELINE</p>
                <p className="font-display text-3xl md:text-4xl font-bold leading-tight">{p.title}</p>
                <p className="meta-mono mt-3" style={active === p.key ? { color: "white", opacity: 0.8 } : {}}>{p.verticals.join(" · ")}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-16">
          <SectionLabel>{cur.title}</SectionLabel>
          <p className="font-display text-3xl md:text-5xl font-bold leading-tight mb-10">{cur.blurb}</p>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-px bg-border">
            <F label="Your name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="dist-name" />
            <F label="Email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} testid="dist-email" />
            <F label="Discipline" v={form.discipline} on={(v) => setForm({ ...form, discipline: v })} testid="dist-discipline" />
            <F label="Title of the work" v={form.title} on={(v) => setForm({ ...form, title: v })} testid="dist-title" />
            <F label="Link (drive / Bandcamp / vimeo)" v={form.link} on={(v) => setForm({ ...form, link: v })} testid="dist-link" />
            <div className="bg-background p-6 flex flex-col justify-end">
              <p className="meta-mono mb-3">— REVIEW CYCLE</p>
              <p className="font-display text-2xl font-bold">Weekly</p>
            </div>
            <div className="md:col-span-2 bg-background p-6">
              <p className="meta-mono mb-3">— DESCRIPTION</p>
              <textarea data-testid="dist-desc" rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-transparent outline-none text-base font-light border-b border-border focus:border-primary py-3" />
            </div>
            <div className="md:col-span-2 bg-background p-6 flex justify-end">
              <button data-testid="dist-submit" disabled={submitting} className="btn-pill btn-coral">{submitting ? "Sending…" : <>{cur.cta} <ArrowRight size={14} /></>}</button>
            </div>
          </form>
        </div>
      </section>
    </PageShell>
  );
}
function F({ label, v, on, type = "text", testid }) {
  return (
    <div className="bg-background p-6">
      <p className="meta-mono mb-3">— {label.toUpperCase()}</p>
      <input data-testid={testid} type={type} value={v} onChange={(e) => on(e.target.value)} className="w-full bg-transparent outline-none text-lg font-light border-b border-border focus:border-primary py-2" />
    </div>
  );
}
